import { isSiteQuiet, resolveWpMode } from '@guard-dog/contracts';
import { useEffect } from 'react';
import { api, type ConsoleSite } from '../api/client';
import { Async } from '../components/Async';
import { ConnectSite } from '../components/ConnectSite';
import { FirstRunOnboarding } from '../components/FirstRunOnboarding';
import { SiteDiagnosis } from '../components/SiteDiagnosis.js';
import { firstRunComplete, shouldShowFirstRun, useFirstRunProgress } from '../lib/first-run';
import { useFetch } from '../useFetch';

function modeBadge(site: ConsoleSite): string {
  if (!site.edgeHostname) return 'not on edge';
  return site.edgeMode ?? 'unknown';
}

/** The fleet dashboard: every site in the operator's tenant, linking to its detail. */
export function Fleet({
  tenantId,
  canEdit,
  canAddProtection,
}: {
  tenantId: string;
  canEdit: boolean;
  canAddProtection: boolean;
}) {
  const state = useFetch(() => api.sites());
  const { progress, update } = useFirstRunProgress(tenantId);
  const firstSite = state.data?.sites[0];
  const firstSiteId = firstSite?.id;
  const siteCount = state.data?.count ?? 0;
  const showFirstRun = Boolean(
    tenantId && canEdit && state.data && shouldShowFirstRun(siteCount, progress),
  );
  const inventory = useFetch(
    () =>
      showFirstRun && firstSiteId
        ? api.inventory(firstSiteId)
        : Promise.resolve({ items: [], count: 0 }),
    `${showFirstRun ? 'watch' : 'idle'}:${firstSiteId ?? 'no-first-site'}`,
  );
  const setupComplete = firstRunComplete(firstSite, inventory.data?.count ?? 0);

  useEffect(() => {
    if (tenantId && canEdit && state.data?.count === 0 && !progress.started) {
      update({ started: true, dismissed: false });
    }
  }, [canEdit, progress.started, state.data?.count, tenantId, update]);

  useEffect(() => {
    if (!showFirstRun || setupComplete) return;
    const timer = window.setInterval(() => {
      state.reload();
      if (firstSiteId) inventory.reload();
    }, 5000);
    return () => window.clearInterval(timer);
  }, [firstSiteId, inventory.reload, setupComplete, showFirstRun, state.reload]);

  function refreshFirstRun() {
    state.reload();
    if (firstSiteId) inventory.reload();
  }

  return (
    <section>
      <div className="section-head">
        <h2>Fleet</h2>
        {canEdit && !showFirstRun && <ConnectSite canMintToken={canAddProtection} />}
      </div>
      <Async state={state}>
        {({ sites }) => (
          <>
            {showFirstRun && (
              <FirstRunOnboarding
                progress={progress}
                updateProgress={update}
                site={firstSite}
                inventoryCount={inventory.data?.count ?? 0}
                inventoryError={inventory.error}
                canMintToken={canAddProtection}
                onRefresh={refreshFirstRun}
              />
            )}
            {sites.length === 0 && !showFirstRun ? (
              <p className="muted">No sites enrolled yet.</p>
            ) : sites.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>Site</th>
                    <th>Edge</th>
                    <th>WordPress</th>
                    <th>Connector</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {sites.map((s) => (
                    <tr key={s.id}>
                      <td>
                        <a href={`#/sites/${s.id}`}>{s.url}</a>
                      </td>
                      <td>
                        <span className={`badge badge-${modeBadge(s).replace(/\s+/g, '-')}`}>
                          {modeBadge(s)}
                        </span>
                      </td>
                      {/* Never `s.wpMode ?? 'unknown'`: an unset column means the plugin's engine is
                          running and recording, which is the state of every site in the fleet today.
                          `resolveWpMode` is the same three lines the api and the connector use, so
                          all three planes read one absent value the same way (ADR-0030 §2). */}
                      <td>
                        <span className={`badge badge-${resolveWpMode(s.wpMode)}`}>
                          {resolveWpMode(s.wpMode)}
                        </span>
                      </td>
                      {/* The build each site last REPORTED — refreshed on every heartbeat and every
                          successful liveness poll, so it tracks upgrades without a re-enroll. A site
                          that has not reported since this shipped genuinely has an unknown version,
                          and "—" says so rather than guessing. */}
                      <td className="mono">{s.agentVersion ?? <span className="muted">—</span>}</td>
                      <td className="muted">
                        {s.lastSeenAt ?? '—'}
                        {/* Offered only where it can help. A stale timestamp is the least
                            informative fact here: on 2026-08-03 three unrelated causes produced an
                            identical one on real sites, none of them a security event. */}
                        {isSiteQuiet(s.lastSeenAt) ? <SiteDiagnosis siteId={s.id} /> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </>
        )}
      </Async>
    </section>
  );
}
